import React, {useRef, useCallback, memo, useMemo, useEffect, useState} from "react";
import {GoogleMap, Marker, useJsApiLoader} from "@react-google-maps/api";
import styles from "./styles.module.scss";
import Chip from "@/app/components/chip";
import LazyAssets from "@/app/utils/assets_widget";
import Input from "@/app/components/input";
import classNames from "classnames";
import Button from "@/app/components/button";
import {s} from "@/app/utils/s";
import {t} from "i18next";
import {CourtBigModel, CourtModel, CourtsRequestModel, courtTypeValues} from "@/app/api/courts";
import {ReviewModel} from "@/app/api/reviews";
import CircularProgressIndicator from "@/app/components/circular_progress_indicator";
import ZoomWidget from "@/app/components/zoom_widget";

const containerStyle = {
  width: "100%",
  height: "100%",
};

const mapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  gestureHandling: "auto",
};

interface MapWithMarkersProps {
  center: { lat: number; lng: number };
  markers: CourtModel[];
  court?: CourtBigModel;
  reviews?: ReviewModel[];
  courtLoading: boolean;
  onSelectCourt: (id?: number) => void;
  applyFilters: (filters: CourtsRequestModel) => void;
  loadMoreCourts: (filters?: CourtsRequestModel) => Promise<CourtModel[]>;
  state: any;
  paginationState: any;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  setType: any;
  setBooking: any;
  setArtificialLight: any;
  setLockerRooms: any;
  setParkingAvailability: any;
  setMinRating: any;
  clearFilters: any;
  toggleFilters: any;
  calculateFilterCount: any;
  toggleCreateCourt: any;
  setCreatedMarker: any;
  isDesktop: boolean;
}

const MapWithMarkers: React.FC<MapWithMarkersProps> = memo((props) => {
  const {
    center,
    markers,
    onSelectCourt,
    applyFilters,
    loadMoreCourts,
    state,
    paginationState,
    searchTerm,
    onSearchChange,
    setType,
    toggleFilters,
    calculateFilterCount,
    toggleCreateCourt,
    setCreatedMarker,
    isDesktop
  } = props;

  const {isLoaded, loadError} = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const centerRef = useRef<google.maps.LatLng | null>();
  const [zoom, setZoom] = React.useState(10);
  const [mapCenter, setMapCenter] = React.useState(center);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!state.showCreateCourt) return;
    if (e.latLng) {
      setCreatedMarker({
        lat: e.latLng.lat(),
        lon: e.latLng.lng()
      });
    }
  }, [setCreatedMarker, state.showCreateCourt]);

  const filterCount = useMemo(() => calculateFilterCount(), [calculateFilterCount, state.filters]);

  const TypeFilters = useMemo(
    () => (
      <>
        {courtTypeValues.map((type) => (
          <Chip
            key={type}
            text={t(type.replaceAll("_", " "))}
            isActive={state.filters.type === type}
            onClick={() => setType(type, applyFilters, {applyFilterCount: true})}
          />
        ))}
      </>
    ),
    [applyFilters, setType, state.filters.type]
  );

  if (loadError) return <div>Error loading map</div>;
  if (!isLoaded) return <CircularProgressIndicator/>;

  return (
    <div className={styles.mapContainer}>
      {!state.showCreateCourt && (
        <div className={styles.searchBar}>
          <div className={styles.input}>
            <Input
              onChange={onSearchChange}
              hint={t(s.search)}
              prefixIcon={<LazyAssets.Search/>}
              value={searchTerm}
            />
          </div>
          <div className={styles.filters}>
            <div className={styles.filterWrapper}>
              <Chip
                child={<LazyAssets.Filter
                  className={classNames(styles.filterIcon, {[styles.active]: filterCount > 0})}/>}
                onClick={toggleFilters}
                isActive={filterCount > 0}
              />
              {filterCount > 0 && <span className={styles.filterCount}>{filterCount}</span>}
            </div>
            {TypeFilters}
          </div>
        </div>
      )}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={state.showCreateCourt && state.pointSelected ? {lat: state.pointSelected.lat, lng: state.pointSelected.lon} : mapCenter}
        onLoad={onMapLoad}
        options={mapOptions}
        zoom={zoom}
        onClick={handleMapClick}
      >
        {!state.showCreateCourt && markers.map((marker) => (
          <Marker
            key={marker.id}
            position={{lat: marker.latitude, lng: marker.longitude}}
            onClick={() => onSelectCourt(marker.id)}
            icon={"/map.svg"}
          />
        ))}
        {state.showCreateCourt && state.pointSelected && <Marker
            position={{lat: state.pointSelected.lat, lng: state.pointSelected.lon}}
            icon={"/map.svg"}
        />}
      </GoogleMap>
      {!state.showCreateCourt && (
        <Button className={styles.addButton} onClick={toggleCreateCourt} icon={<LazyAssets.Add/>}
                text={isDesktop ? t(s.addAPadelPlace) : undefined} circle={!isDesktop}/>
      )}
      <ZoomWidget zoom={zoom} setZoom={setZoom} className={styles.zoomWidget}/>
    </div>
  );
});

export default MapWithMarkers;
